import { useMemo } from "react";
import { useFetch } from "@/hooks/FetchHook";
import { constructQueryString } from "@/components/UI/TableToolbar/Filtering/filtersApiUtil";
import type { TableMetadataType } from "@/types/tableMetadata";

/**
 * Picks the first of a priority-ordered list of filter keys whose metadata
 * has any values — e.g. a newer eCRF page-name column, falling back to a
 * legacy grouping column when the dataset predates it.
 */
export function getDatasetPageNames(
  filters: TableMetadataType["filters"] | undefined,
  pageFilterKeys: string[],
): { pageNames: string[]; filterKey: string | undefined } {
  if (!filters) return { pageNames: [], filterKey: undefined };

  for (const key of pageFilterKeys) {
    const values = filters[key]?.values;
    const pageNames = values
      ?.filter((value) => value !== null && String(value).trim().length > 0)
      .map(String);
    if (pageNames && pageNames.length > 0) {
      return { pageNames, filterKey: key };
    }
  }

  return { pageNames: [], filterKey: undefined };
}

/**
 * Discovers a tabbed dataset page's tab values by reusing the same metadata
 * query `DashboardFetchWrapper` issues for the page itself (identical
 * endpoint/query-key, so TanStack Query serves it from cache rather than
 * fetching twice).
 */
export function useDatasetPageNames(
  datasetName: string,
  pageFilterKeys: string[],
  subjectId?: string,
) {
  const queryString = constructQueryString({
    baseParams: { dataset_type: datasetName },
    filters: subjectId ? { subject: [subjectId] } : {},
  });

  const metadataQuery = useFetch({
    endpointSuffix: `filters/${queryString}`,
    extraQueryKey: subjectId,
  });

  const { pageNames, filterKey } = useMemo(
    () =>
      getDatasetPageNames(
        (metadataQuery.data as TableMetadataType | undefined)?.filters,
        pageFilterKeys,
      ),
    [metadataQuery.data, pageFilterKeys],
  );

  return { pageNames, filterKey, isLoading: metadataQuery.isLoading };
}
